import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    TextField,
    MenuItem,
    InputAdornment,
    Button,
    Snackbar, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonIcon from '@mui/icons-material/Person';
import MailIcon from '@mui/icons-material/Mail';
import { useAppDispatch } from '../../redux/store';
import { useSelector } from 'react-redux';
import { selectConfig } from '../../redux/slices/configSlice';
import { selectPlayers } from '../../redux/slices/playersSlice';
import { signInTwilio } from '../../redux/slices/userSlice';
import { setPage } from '../../redux/slices/pageSlice';
import { getUrlParam, capitalizeWords, getUrlElement } from '../../utils/common';

import { sessionDataHandler } from '../../sessionDataHandler';
import { changeEngagementPhase } from '../../twilio/store/genericActions';
import { EngagementPhase } from '../../twilio/store/definitions';
import { initSession } from '../../twilio/store/initSession';
import { addNotification } from '../../twilio/store/genericActions';
import { notifications } from '../../twilio/notifications';
import type { FormEvent } from 'react';

// Define types for the state and props
interface ErrorState {
    username?: string;
    email?: string;
    inquiry?: string;
    lastName?: string;
    deposit?: string;
    product1?: string;
}

interface InquiryOption {
    Label: string;
    value: string;
    display: boolean;
}

interface ProductOption {
    Brandname: string;
    Value: string;
}
interface User {
    brand: string;
    level: string;
    lang: string;
    country: string;
    product: string;
    inquiry: string;
    source: string;
    payment: string;
    opm: string;
    init_msg?: string;
    referer: string;
    refererPlatform: string;
    cat: string;
}

const useIpAndCountry = () => {
    const [ipAddress, setIpAddress] = useState('');
    const [countryCode, setCountryCode] = useState('');

    useEffect(() => {
        const fetchIpAndCountry = async () => {
            try {
                // Primary service - ipapi.co (higher rate limits)
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();

                if (data.ip && data.country) {
                    setIpAddress(data.ip);
                    setCountryCode(data.country_code);
                    return;
                }

                // Fallback 1 - cloudflare
                const cfResponse = await fetch('https://cloudflare.com/cdn-cgi/trace');
                const cfText = await cfResponse.text();

                if (cfText) {
                    // Define the type for dataObj
                    const dataObj: Record<string, string> = cfText.trim().split('\n').reduce((obj, pair) => {
                        const parts = pair.split('=');
                        if (parts.length === 2) {
                            const [key, value] = parts;
                            return { ...obj, [key]: value };
                        }
                        return obj;
                    }, {} as Record<string, string>);

                    if (dataObj.ip) {
                        setIpAddress(dataObj.ip);
                        setCountryCode(dataObj.loc || '');
                        return;
                    }
                }

                // Fallback 2 - geolocation-db.com
                const geoResponse = await fetch('https://geolocation-db.com/json/');
                const geoData = await geoResponse.json();

                if (geoData.IPv4) {
                    setIpAddress(geoData.IPv4);
                    setCountryCode(geoData.country_code);
                }
            } catch (error) {
                console.error('Error fetching IP and Country Code:', error);
                // Last resort - use client-side detection if available
                try {
                    const rtcPeerConnection = new RTCPeerConnection({iceServers: []});
                    rtcPeerConnection.createDataChannel('');
                    rtcPeerConnection.createOffer().then(offer => rtcPeerConnection.setLocalDescription(offer));
                    rtcPeerConnection.onicecandidate = (ice) => {
                        if (ice.candidate) {
                            const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3})/;
                            const match = ipRegex.exec(ice.candidate.candidate);
                            if (match) {
                                setIpAddress(match[1]);
                                rtcPeerConnection.close();
                            }
                        }
                    };
                } catch (e) {
                    console.error('Failed to get IP via WebRTC:', e);
                }
            }
        };

        fetchIpAndCountry();
    }, []);

    return { ipAddress, countryCode };
};

const PreChat: React.FC = () => {
    const [username, setUsername] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [inquiry, setInquiry] = useState<string>('');
    const [subinquiry, setSubinquiry] = useState<string>('');
    const [product1, setProduct1] = useState<string>('');
    const [deposit, setDeposit] = useState<string>('');
    const [depositField, setDepositField] = useState<boolean>(false);
    const [depositPlaceholder, setDepositPlaceholder] = useState<string>('');
    const [expanded, setExpanded] = useState<string | false>(false);
    const [errors, setErrors] = useState<ErrorState>({});
    const { ipAddress, countryCode } = useIpAndCountry();
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(false)
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');

    const dispatch = useAppDispatch();

    const config = useSelector(selectConfig);
    const formData = config?.data?.[0]?.attributes;

    // Nextbet Brasil has stopped operating, so its chat form can no longer be submitted
    const isNextbetBrClosed =
        (getUrlElement(6) || '').toLowerCase() === 'pt' && (getUrlElement(7) || '').toLowerCase() === 'nextbet';


    const players = useSelector(selectPlayers);
    const playerData = players?.data ? players?.data.attributes : null;

    let lang: string = "EN";
    let locale = "";

    const langMap: Record<string, string> = {
    "en-IN": "in",
    "kk": "kz",
    "zh-Hans": "cn",
    "ko-KR": "kr",
    "vi": "vn",
    "ne": "np",
    "zh-Hant": "cn",
    "bn-BD": "bd",
    "en-PK": "pk",
    // Add more mappings as needed
    };

    // Add a check for config before accessing its properties
    if (config && Array.isArray(config.data) && config.data.length > 0) {
        locale = config.data[0].attributes.locale;
    } else {
        console.error("config.data is not an array or is empty or config is null/undefined");
    }

    lang = locale ?? lang;
    lang = langMap[ locale] ?? lang;
    lang = lang.toUpperCase();


    const productOptions: ProductOption[] =
        formData?.PreChatSurvey?.ProductList?.filter(
            (option: ProductOption) => option
        ) || [];
    // Ensure inquiryOptions is typed and filtered for display
    const inquiryOptions: InquiryOption[] =
        formData?.PreChatSurvey?.InquiryandSubinquiryList?.filter(
            (option: InquiryOption) => option.display
        ) || [];

    const subinquiryListNew = [
        { value: "", name: formData?.PreChatSurvey?.EmptySubInquiryLabel }
    ];

    const [showSubInq, setShowSubInq] = useState(
        formData?.PreChatSurvey?.HideSubInquiry === "1"
    );

    useEffect(() => {
        // Prioritize username from query string, then playerData
        const queryUsername = getUrlParam('username');
        setUsername(queryUsername || playerData?.username || '');
        setEmail(playerData?.email || '');
    }, [playerData]);

    const isValidUsername = (username: string) => /^[a-zA-Z0-9_]{3,16}$/.test(username);
    const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

    const validate = (): boolean => {
        const newErrors: ErrorState = {};
        let isValid = true;

        const {
            UsernameRequired,
            UsernameErrorMessage,
            LastNameErrorMessage,
            EmailRequired,
            EmailErrorMessage,
            InquiryRequired,
            InquiryErrorMessage,
            ProductRequired,
            ProductErrorMessage,
            ShowLastname
        } = formData?.PreChatSurvey || {};

        const addError = (key: keyof ErrorState, message: string) => {
            newErrors[key] = message;
            isValid = false;
        };

        // Username
        if (UsernameRequired && !username) {
            addError('username', UsernameErrorMessage ?? 'Username is required');
        } else if (username && !isValidUsername(username)) {
            addError('username', UsernameErrorMessage ?? 'Invalid username format');
        }

        // Last name
        if (ShowLastname && !lastName) {
            addError('lastName', LastNameErrorMessage ?? 'Last name is required');
        }

        // Email
        if (!email) {
            if (EmailRequired) {
                addError('email', EmailErrorMessage ?? 'Email is required');
            }
        } else if (!isValidEmail(email)) {
            addError('email', EmailErrorMessage ?? 'Invalid email format');
        }

        // Inquiry
        if (InquiryRequired && !inquiry) {
            addError('inquiry', InquiryErrorMessage ?? 'Please select an inquiry type');
        }

        // Product
        if (ProductRequired && !product1) {
            addError('product1', ProductErrorMessage ?? 'Please select a product type');
        }

        // Deposit
        if (depositField && !deposit) {
            addError('deposit', 'Deposit amount is required');
        } else if (depositField && deposit && isNaN(Number(deposit))) {
            addError('deposit', 'Please enter a valid number');
        }

        setErrors(newErrors);
        return isValid;
    };

    const generateUserLevel = function() {
        let level = 'REG';
        let levelInt = getUrlParam('level');

        if(levelInt) {
            if(["17", "26"].includes(levelInt)) {
            level = "VIP"
            } else if(["13", "18"].includes(levelInt)) {
            level = "Elite"
            } else {
            level = "REG"
            }
        }
        return level;
    }

    const appGenerateSkillset = (user: User): string => {

        let cat = user.cat;
        cat = capitalizeWords(cat)
        if(cat === "Dafabet") {
            cat = "Dafa";
        }

        let options = [
                    'WC',
                    lang,
                    cat,
                    '__REG_PI'
                ];

        return options.join('_');
    }

    const handleInquiryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const selectedValue = event.target.value as string;
        setInquiry(selectedValue);

        // Remove validation error when user selects a valid value
        setErrors((prevErrors) => ({
            ...prevErrors,
            inquiry: selectedValue ? "" : prevErrors.inquiry,
        }));

        // Check if there is a subinquiry for the selected inquiry
        if (formData?.PreChatSurvey?.subinquiry?.[selectedValue]) {
          setShowSubInq(true);
        } else {
          setShowSubInq(false);
        }

        // Handle deposit field logic
        if (selectedValue === "DP" && lang === "KR") {
            setDepositField(true);
        } else if (selectedValue === "HA") {
            setDepositField(true);
            setDepositPlaceholder(config?.data?.[0]?.attributes?.PreChatSurvey?.depositPlaceholderHA || '');
        } else {
            setDepositField(false);
        }
    };

    const getReferer = (defaultValue = '') => {
        let referer = document.referrer;

        if (!referer || referer === '') {
            const urlParams = new URLSearchParams(window.location.search);
            referer = urlParams.get('referer') || defaultValue;
        }

        try {
            const url = new URL(referer);
            return `${url.protocol}//${url.host}${url.pathname}`;
        } catch (error) {
            return defaultValue;
        }
    };

    const preventNonNumericalInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const charCode = event.which ? event.which : event.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            event.preventDefault();
        }
    };

    const normalizedUsername = username
        ?.trim()
        .toLowerCase();

    const handleSubmit = (): void => {
        if (validate()) {
        setSubmitDisabled(true)
            if(locale === "zh-Hans"){
                lang = "CN"
            }

            let user: User = {
                brand: getUrlParam("brand") || "Dafa",
                level: generateUserLevel(),
                lang: lang || "EN",
                country: config.countryCode || "",
                product: product1 !== "" ? product1 : getUrlParam("product") || "",
                inquiry: inquiry !== "" ? inquiry : getUrlParam("inquiry") || "",
                source: "EWC",
                payment: getUrlParam("payment") || "",
                opm: getUrlParam("otherpayment") || "NULL",
                init_msg: config.init_msg,
                referer: config.source || "",
                refererPlatform: navigator.userAgent,
                cat: "",
              };

              // Override source if playtech is enabled
              if (config.playtech === true) {
                user.source = "Playtech";
              }

              // Define brand mappings
              const brandMapping: Record<string, string> = {
                dfbt: "dafabet",
                sevenbaby: "777baby",
              };

              // Get brand from URL element 7
              let brand = getUrlElement(7);

              // If brand is not "df888" or "df888-gold", apply the mapping logic
              if (brand !== "df888" && brand !== "df888-gold") {
                brand = brandMapping[formData?.Template?.Brand || ""] || formData?.Template?.Brand || brand;
              }

              // Convert product to lowercase
              let product = user.product.toLowerCase();

              // Conditional logic for setting inquiry based on brand and language
              if (brand === "zedbet" && (lang === "EN" || lang === "KR")) {
                user.inquiry = "GI";
              }

              // Fix product name formatting
              if (product === "dafabet") {
                product = "Dafabet";
              }

              // Handle specific pathname cases
              const lastPathSegment = window.location.pathname.split("/").pop();
              if (lastPathSegment === "dafa-spain") {
                brand = "dafabetes";
              }

              // Assign category brand
              user.cat = brand || "Dafa";

              // Handle special hostname case
              if (window.location.hostname === "istg-chat.elysium-dfbt.com" || window.location.hostname === 'localhost') {
                brand = `istg-${brand}`;
                lang = `ISTG ${lang}`;
              }
              console.log(locale, "LOCALE");
              console.log(lang, "LANGUAGE");

              // Generate skillset based on user
              let skillset = appGenerateSkillset(user);

            const browserLanguage = navigator.language;

            const customValues = [
                { title: 'IP', value: ipAddress || '202.151.35.161' },
                { title: 'country', value: countryCode || '' },
                { title: 'referer', value: getReferer() || '' },
                { title: 'RefererPlatform', value: navigator.userAgent },
                { title: 'CustomerVerified', value: playerData ? "true" : "false" },
                { title: 'language', value: (lang || 'EN').toUpperCase() },
                { title: 'browserLanguage', value: browserLanguage },
                { title: 'CustomerSegment', value: user.level },  // Replace with actual user level if available
                { title: 'SuggestedPM', value: user.payment },  // Replace with actual payment method if available
                { title: 'OtherPM', value: user.opm }, // Replace if needed
                { title: 'Restart', value: 'false' },
                { title: 'skillset', value: skillset },
                { title: 'username', value: normalizedUsername },
                { title: 'name', value: normalizedUsername },
                { title: 'email', value: email },
                { title: 'phoneNumber', value: '' },
                { title: 'product', value: product },
                { title: 'inquiry', value: inquiry },
                { title: 'subinquiry', value: subinquiry },  // Replace with actual subinquiry if available
                { title: 'deposit', value: deposit }
            ];

            // Add additional product information if available
            if (product) {
                customValues.push({ title: 'Product', value: product });
            }

            let engagementParameters: Record<string, any> = {
                Brand: brand, // ui.config.brand
            };

            // Populate engagementParameters with each custom value
            customValues.forEach(item => {
                engagementParameters[item.title] = item.value;
            });

            // Prepare Twilio pre-engagement form data
            const twilioFormData = {
                friendlyName: normalizedUsername || 'Customer',
                email: email || undefined,
                query: (formData?.ChatBox?.HelloMessageText ?? 'Hello'),
                ...engagementParameters
            };


            dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
            dispatch(signInTwilio({ formData: twilioFormData }))
                .then((result: any) => {
                    if (result.meta.requestStatus === 'fulfilled') {
                        const { token, conversationSid } = result.payload.userData || {};
                        if (token && conversationSid) {
                            dispatch(initSession({ token, conversationSid }) as any);
                        }
                        dispatch(setPage('chat'));
                    } else {
                        const error = result?.payload?.data;
                        if (!error?.success) {
                            setMessage(error?.message);
                            setOpen(true);
                            setSubmitDisabled(false);
                            return;
                        }
                        dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                    }
                });
        }
    };

    const handleChange = (panel: string) => (
        event: React.SyntheticEvent,
        isExpanded: boolean
    ): void => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box p={3}>
            <Snackbar
                open={open}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }} 
            >
                <Alert severity="info" onClose={() => setOpen(false)}>
                    {message}
                </Alert>
            </Snackbar>
            {formData?.Announcement?.trim() && (
                <Accordion
                    expanded={expanded === 'panel1'}
                    onChange={handleChange('panel1')}
                    sx={{ border: '1px dotted', padding: '10px 0' }}
                >
                    <AccordionSummary
                        expandIcon={expanded === 'panel1' ? <RemoveIcon /> : <AddIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography sx={{ color: 'secondary.main', paddingLeft: '50px' }} component="h2" variant="h2">
                            <CampaignIcon
                                sx={{
                                    position: 'absolute',
                                    left: '10px',
                                    transform: 'rotate(-45deg)',
                                    fontSize: '40px',
                                }}
                            />{' '}
                            {formData?.AnnouncementTitle || 'Announcement'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography dangerouslySetInnerHTML={{ __html: formData.Announcement }} />
                    </AccordionDetails>
                </Accordion>
            )}

            <Typography component="h2" variant="h2" sx={{ margin: '38px 0' }}>
                {formData?.PreChatSurvey?.FormTitle}
            </Typography>


            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    maxWidth: '950px',
                    margin: '0 auto',
                }}
            >

                {isNextbetBrClosed && (
                    <Alert severity="warning" sx={{ width: '100%', marginTop: '20px', boxSizing: 'border-box' }}>
                        A Nextbet Brasil não está mais em operação. Por favor, solicite o saque de quaisquer fundos
                        restantes em sua conta. Caso tenha algum problema com o saque, entre em contato conosco pelo
                        e-mail <a href="mailto:suporte@nextbetbr.com">suporte@nextbetbr.com</a>.
                    </Alert>
                )}

                {!formData?.PreChatSurvey?.hideUsername && <TextField
                    id="username"
                    placeholder={formData?.PreChatSurvey?.UsernamePlaceholder || 'Enter your username'}
                    disabled={playerData !== null || isNextbetBrClosed}
                    variant="outlined"
                    sx={{
                        backgroundColor: '#E5E5EB',
                        marginTop: '20px',
                        '& .Mui-focused': {
                            backgroundColor: '#fff',
                        },
                    }}
                    value={username}
                    onChange={(e) => {
                        const selectedValue = e.target.value;
                        setUsername(selectedValue);

                        // Remove validation error when user selects a valid value
                        setErrors((prevErrors) => ({
                            ...prevErrors,
                            username: selectedValue ? "" : prevErrors.username,
                        }));
                    }}
                    error={!!errors.username}
                    helperText={errors.username}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PersonIcon />
                            </InputAdornment>
                        ),
                    }}
                />}

                {formData?.PreChatSurvey?.ShowLastname && (
                    <TextField
                        disabled={isNextbetBrClosed}
                        id="lastName"
                        placeholder={formData?.PreChatSurvey?.LastnamePlaceholder || 'Enter your last name'}
                        variant="outlined"
                        sx={{
                            backgroundColor: '#E5E5EB',
                            marginTop: '20px',
                            '& .Mui-focused': {
                                backgroundColor: '#fff',
                            },
                        }}
                        value={lastName}
                        onChange={(e) => {
                            const selectedValue = e.target.value;
                            setLastName(selectedValue);

                            // Remove validation error when user selects a valid value
                            setErrors((prevErrors) => ({
                                ...prevErrors,
                                lastName: selectedValue ? "" : prevErrors.lastName,
                            }));
                        }}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PersonIcon />
                            </InputAdornment>
                        ),
                    }}
                    />
                )}

                {!formData?.PreChatSurvey?.HideEmail && (
                    <TextField
                        disabled={isNextbetBrClosed}
                        id="email"
                        placeholder={formData?.PreChatSurvey?.EmailPlaceholder || 'Enter your email'}
                        variant="outlined"
                        sx={{
                            margin: '20px 0 0 0',
                            backgroundColor: '#E5E5EB',
                            '& .Mui-focused': {
                                backgroundColor: '#fff',
                            },
                            position: `${playerData !== null ? "absolute" : "relative"}`,
                            left: `${playerData !== null ? "-9999px" : "0"}`,
                            // display: `${playerData !== null ? "none" : "block"}`
                        }}
                        value={email}
                        onChange={(e) => {
                            const selectedValue = e.target.value;
                            setEmail(selectedValue);

                            // Remove validation error when user selects a valid value
                            setErrors((prevErrors) => ({
                                ...prevErrors,
                                email: selectedValue ? "" : prevErrors.email,
                            }));
                        }}
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MailIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {!formData?.PreChatSurvey?.HideProduct && (
                    <TextField
                        disabled={isNextbetBrClosed}
                        id="product1"
                        label={productOptions.length > 0 ? productOptions[0].Brandname : "Select an option"}
                        select
                        variant="outlined"
                        sx={{
                            margin: '20px 0 0 0',
                            backgroundColor: '#E5E5EB',
                            '& .Mui-focused': {
                                backgroundColor: '#fff',
                            },
                        }}
                        value={product1}
                        onChange={(e) => {
                            const selectedValue = e.target.value;
                            setProduct1(selectedValue);

                            // Remove validation error when user selects a valid value
                            setErrors((prevErrors) => ({
                                ...prevErrors,
                                product1: selectedValue ? "" : prevErrors.product1,
                            }));
                        }}
                        error={!!errors.product1}
                        helperText={errors.product1}
                    >
                        {productOptions.map((option) => (
                            <MenuItem key={option.Value} value={option.Value}>
                                {option.Brandname}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {!formData?.PreChatSurvey?.HideInquiry && (<TextField
                    disabled={isNextbetBrClosed}
                    id="inquiry"
                    label={inquiryOptions.length > 0 ? inquiryOptions[0].Label : "Select an option"}
                    select
                    value={inquiry}
                    onChange={(e) => handleInquiryChange(e)}
                    fullWidth
                    sx={{
                        margin: '20px 0 0 0',
                        backgroundColor: '#E5E5EB',
                        '& .Mui-focused': {
                            backgroundColor: '#fff',
                        },
                    }}
                    InputLabelProps={{
                        sx: {
                            color: '#a0a0a4',
                            '&.Mui-focused': {
                                color: '#a0a0a4',
                            },
                        },
                    }}
                    error={!!errors.inquiry}
                    helperText={errors.inquiry}
                >
                    {inquiryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.Label}
                        </MenuItem>
                    ))}
                </TextField>
                )}

                {showSubInq && (
                    <TextField
                        disabled={isNextbetBrClosed}
                        id="subinquiry"
                        label="Sub Inquiry"
                        select
                        value={subinquiry || ""}
                        onChange={(e) => setSubinquiry( e.target.value)}
                        onBlur={() => setSubinquiry(subinquiry?.trimEnd())}
                        fullWidth
                        sx={{
                            backgroundColor: "#E5E5EB",
                            "& .Mui-focused": {
                            backgroundColor: "#fff",
                            },
                        }}
                        >
                        {subinquiryListNew.map((subinquiry) => (
                            <MenuItem key={subinquiry.value} value={subinquiry.value}>
                            {subinquiry.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {((config?.data?.[0]?.attributes?.PreChatSurvey?.ShowDeposit === true) && depositField) && (
                    <TextField
                        disabled={isNextbetBrClosed}
                        id="deposit"
                        label="deposit"
                        type="number"
                        value={deposit}
                        onChange={(e) => {
                            setDeposit(e.target.value);
                            // Remove validation error when user enters a value
                            setErrors((prevErrors) => ({
                                ...prevErrors,
                                deposit: e.target.value ? "" : prevErrors.deposit,
                            }));
                        }}
                        onKeyPress={preventNonNumericalInput}
                        onBlur={() => setDeposit(deposit.trimEnd())}
                        placeholder={depositPlaceholder}
                        fullWidth
                        sx={{
                            margin: '20px 0 0 0',
                            backgroundColor: '#E5E5EB',
                            '& .Mui-focused': {
                                backgroundColor: '#fff',
                            },
                        }}
                        InputLabelProps={{
                            sx: {
                                color: '#a0a0a4',
                                '&.Mui-focused': {
                                    color: '#a0a0a4',
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <img
                                        className="input-icon deposit"
                                        src="data:image/png;base64,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"
                                        alt="deposit icon"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        error={!!errors.deposit}
                        helperText={
                            <>
                                {errors.deposit}
                                {((config?.data?.[0]?.attributes?.PreChatSurvey?.ShowDepositDescription === "1")) && (
                                    <div style={{ marginTop: '8px', fontSize: '0.875rem', color: '#666' }}>
                                        <span>새로운 입금방식 가이드를 <a href="https://m.nonggufun.com/kr/lbt-self-guide" target="_blank" rel="noopener noreferrer">여기서</a> 확인해보세요!</span>
                                    </div>
                                )}
                            </>
                        }
                    />
                )}

                <Button
                    variant="contained"
                    disabled={submitDisabled || isNextbetBrClosed}
                    onClick={handleSubmit}
                    loading={submitDisabled}
                    sx={{
                        bgcolor: 'customYellow.main',
                        color: '#000',
                        width: '100%',
                        padding: '15px 0',
                        marginTop: '20px',
                        fontWeight: 'bold',
                        fontSize: '18px',
                    }}
                >
                    {formData?.PreChatSurvey?.SubmitButtonText || 'Submit'}
                </Button>
            </Box>
        </Box>
    );
};

export default PreChat;
